import { CurrencySettings, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { IncludeExcludeList } from "@mcleod/dispatch/src/planning/PlanningProfileFilter";
import { getPayrollControlValue } from "@mcleod/general";
import { PayablesLookup } from "@mcleod/settlement/src/OrderPayables";

export class OfficePayrollProcessingFilter {
    private filterRow: ModelRow;
    public payablesLookupMap: PayablesLookup;
    private includeLists: IncludeExcludeList[] = [];
    private startDate: Date;
    private cutOffDate: Date;

    constructor(filterRow: ModelRow, cutoffDate?: Date) {
        this.filterRow = filterRow;
        this.addIncludeExcludeList("payee_ids", "payee_id", "payee_ids_exclude");
        this.addIncludeExcludeList("division_ids", "division_id");
        this.addIncludeExcludeList("terminal_ids", "terminal_id");
        this.addIncludeExcludeList("job_class_codes", "job_class_code_id");
        this.addIncludeExcludeList("payment_method", "payment_method");
        this.addIncludeExcludeList("pay_rate_method", "pay_method");
        if (!filterRow.isNull("start_date"))
            this.startDate = DateUtil.parseDateWithKeywords(filterRow.get("start_date"), true, false);
        if (cutoffDate) {
            this.cutOffDate = cutoffDate;
        }
        else if (!filterRow.isNull("cutoff_date")) {
            this.cutOffDate = DateUtil.parseDateWithKeywords(filterRow.get("cutoff_date"), true, false);
        } else {
            this.cutOffDate = getPayrollControlValue("off_cutoff_date") ? new Date(getPayrollControlValue("off_cutoff_date")) : null;
        }
    }

    public dataPasses(data: ModelRow): boolean {
        if (data.get("ready to pay flag") === "V" || data.get("record_type") !== "O") {
            return false;
        }
        if (!this.filterRow.isNull("pay_adjustment_codes")) {
            const payAdjustmentCodes: [] = this.filterRow.get("pay_adjustment_codes");
            if (payAdjustmentCodes.length > 0 && !this.findItem(payAdjustmentCodes, data.get("deduct_code_id"))) {
                return false;
            }
        }
        const payAdjustmentIncludeList = this.includeLists.filter((list) => {
            return list.dataField !== "division_id" && list.dataField !== "terminal_id" && list.dataField !== "job_class_code_id";
        });
        for (const list of payAdjustmentIncludeList) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.dateTimeMatches(data)) {
            return false;
        }
        if (!this.payeeStatusMatches(data)) {
            return false;
        }
        if (!this.payFrequencyMatches(data)) {
            return false;
        }
        if (!this.frozenPayablesMatches(data)) {
            return false;
        }
        if (!this.statusMatches(data)) {
            return false;
        }
        if (!this.currencyMatches(data)) {
            return false;
        }
        return true;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    private addIncludeExcludeList(includeExcludeListFieldName, dataFieldName?: string, includeExcludeFlagFieldName?: string) {
        this.includeLists.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName || includeExcludeListFieldName + "_exclude", false),
            values: this.tokenizeField(includeExcludeListFieldName, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    private tokenizeField(field, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return [];
        const result = this.filterRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

    private dateTimeMatches(data: ModelRow): boolean {
        let filterDate: Date = data.getDate("ok2pay_date");
        if (filterDate == null)
            return false;
        if (typeof filterDate === "string") // bummer - was hoping this would have already been a date in the ModelRow
            filterDate = DateUtil.parseDate(filterDate);
        filterDate = DateUtil.justDate(filterDate);
        return ((this.startDate == null || this.startDate <= filterDate)
            && (this.cutOffDate == null || this.cutOffDate >= filterDate));
    }

    private statusMatches(data: ModelRow): boolean {
        return data.get("ready_to_pay_flag") !== "V";
    }

    private payeeStatusMatches(data: ModelRow): boolean {
        const payeeStatus = this.filterRow.get("payee_status");
        if (payeeStatus == null) {
            return true;
        } else {
            return data.get("payee_status") === payeeStatus;
        }
    }

    private payFrequencyMatches(data: ModelRow): boolean {
        const payFrequency = this.filterRow.get("pay_frequency");
        if (payFrequency == null) {
            return true;
        }
        return payFrequency === data.get("pay_frequency");
    }

    private frozenPayablesMatches(data: ModelRow): boolean {
        const frozenPayables = this.filterRow.get("frozen_payables");
        if (frozenPayables == null) {
            return true;
        } else if (frozenPayables === "S" && !data.isNull("freeze_by")) {
            return true;
        } else if (frozenPayables === "E" && data.isNull("freeze_by")) {
            return true;
        }

        return false;
    }

    private currencyMatches(data: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;

        return this.getFilterCurrencyType() == data.getCurrency("pay_amount").getCurrencyCode();
    }

    public getFilterCurrencyType() {
        return "USD"; // office payroll only supports USD at this time
        // return this.filterRow.get("currency_type");
    }

    public isPayeeHourlySalary(data: ModelRow): boolean {
        return data.get("pay_method") === "H" || data.get("pay_method") === "S";
    }

    private payeeCurrencyMatches(data: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;
        return data.get("payee_currency") === "USD";
    }

    public dataPassesPayee(data: ModelRow): boolean {
        if (!data.getBoolean("office_employee", false)) {
            return false;
        }
        for (const list of this.includeLists) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.payeeStatusMatches(data)) {
            return false;
        }
        if (!this.payFrequencyMatches(data)) {
            return false;
        }
        if (!this.frozenPayablesMatches(data)) {
            return false;
        }
        if (!this.payeeCurrencyMatches(data)) {
            return false;
        }
        return true;
    }
}
